import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import Avatar from '@/components/Avatar'
import { Search, X } from 'lucide-react'

/**
 * Shared workspace-member picker. Powers both the 1-on-1 DM flow
 * (mode='single') and the N-person group flow (mode='multi').
 *
 * The exclude list lets callers hide the current user (always) and
 * any already-selected users (multi mode pulls them into chips above
 * the search input). Single mode replaces selection on every click.
 *
 * Search filters by full_name case-insensitively. The query itself
 * returns all profiles in the workspace; the filter is applied
 * client-side because workspaces are small (~20-100 members) and a
 * server-side ILIKE would be a network round-trip per keystroke.
 */

interface UserRow {
  id: string
  full_name: string | null
}

export default function UserPicker({
  workspaceId,
  excludeUserIds,
  mode,
  selectedIds,
  onChange,
  placeholder = 'Search teammates…',
}: {
  workspaceId: string
  excludeUserIds: string[]
  mode: 'single' | 'multi'
  selectedIds: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
}) {
  const [search, setSearch] = useState('')

  const { data: members = [], isLoading } = useQuery<UserRow[]>({
    queryKey: ['workspace-members', workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('workspace_id', workspaceId)
        .order('full_name', { ascending: true })
      if (error) throw error
      return (data || []) as UserRow[]
    },
  })

  const excludedSet = useMemo(() => new Set(excludeUserIds), [excludeUserIds])
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return members.filter(m => {
      if (excludedSet.has(m.id)) return false
      if (mode === 'multi' && selectedSet.has(m.id)) return false
      if (!q) return true
      const name = (m.full_name || '').toLowerCase()
      return name.includes(q)
    })
  }, [members, search, excludedSet, selectedSet, mode])

  const selectedMembers = useMemo(() => {
    return selectedIds
      .map(id => members.find(m => m.id === id))
      .filter((m): m is UserRow => !!m)
  }, [selectedIds, members])

  const handlePick = (id: string) => {
    if (mode === 'single') {
      onChange([id])
    } else {
      onChange([...selectedIds, id])
    }
  }

  const handleUnpick = (id: string) => {
    onChange(selectedIds.filter(s => s !== id))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
      {mode === 'multi' && selectedMembers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {selectedMembers.map(m => (
            <span
              key={m.id}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 100, fontSize: 12, fontWeight: 600, color: 'var(--accent-text)' }}
            >
              {m.full_name || 'Unknown user'}
              <button
                onClick={() => handleUnpick(m.id)}
                aria-label={`Remove ${m.full_name || 'user'}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', padding: 2, display: 'flex' }}
              >
                <X style={{ width: 11, height: 11 }} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder}
          style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 9, background: 'var(--field-bg)', maxHeight: 280 }}>
        {isLoading && (
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '12px 14px', margin: 0 }}>Loading…</p>
        )}
        {!isLoading && filtered.length === 0 && (
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '12px 14px', margin: 0 }}>
            {search ? 'No teammates match your search.' : 'No teammates available.'}
          </p>
        )}
        {filtered.map(m => (
          <button
            key={m.id}
            onClick={() => handlePick(m.id)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Avatar name={m.full_name || 'User'} size={28} variant="contact" />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {m.full_name || 'Unknown user'}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
